import React, { useCallback, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { toast } from "react-toastify"
import AssignmentComponent from "../../Assignment/AssignmentComponent"
import { StudentAssignmentStateProps } from "shared/types/moduleTypes"
import { AssignmentContext, AssignmentContextType, AssignmentSection } from "components/Assignment/AssignmentContext"
import moduleRoutes from "shared/routes/moduleRoutes"
import { startNewSubmission } from "helpers/getSubmissionId"
import { StudentAssignmentResponse } from "shared/types/studentTypes"
import { GuardedLink } from "components/General/GuardedLink"

export class StudentAssignmentState implements StudentAssignmentStateProps {
    assignment: StudentAssignmentResponse
    continueAssessment: boolean
    reworkAssignmentMode: boolean
    submissionId: string
    isReview?: boolean
}

export default function StudentAssignment() {
    const location = useLocation()
    const state = location.state as StudentAssignmentStateProps
    const [onReturn, setOnReturn] = React.useState(null)

    let initActivePart;
    if(state.assignment.topicId) {
        initActivePart = "topic"
    } else if (state.assignment.lessonId || state.assignment.lessonVariety === "Pure Inquiry") {
        initActivePart = "lesson"
    } else {
        initActivePart = "assessment"
    }
    const [activePart, setActivePart] = React.useState<AssignmentSection>(initActivePart)
    const [continueAssessment, setContinueAssessment] = useState<boolean>(state.continueAssessment)
    const [submissionId, setSubmissionId] = useState<string>(state.submissionId ?? null)

    const navigate = useNavigate()
    const goBack = useCallback(() => {
        if (state.assignment.lessonVariety === "Review") {
            navigate("/dashboard/student/reviews", { state: { assignmentId: state.assignment.id } })
        } else {
            navigate("/dashboard/student/assignments")
        }
    }, [navigate, state.assignment])

    React.useEffect(() => {
        moduleRoutes.checkModuleAccess(state.assignment.moduleName ?? undefined)
        .then(userHasAccess => {
            if (!userHasAccess && !state?.isReview) {
                return window.location.href = `/simulations/moduleName=${state.assignment.moduleName}/tab=challenge`
            }
        })
        if (location.state === null) {
            toast.info("Unable to load assignment, please reselect the assignment.")
            navigate("/dashboard/student/assignments")
            return null
        }
    }, [location.state, navigate, state.assignment.moduleName, state.assignment.submissions, state.isReview])

    if (location.state === null) {
        return null
    }

    const restartAssignment = async () : Promise<void> => {
        const id = await startNewSubmission(state.assignment.id)
        setSubmissionId(id)
        setContinueAssessment(false)
    }

    const assignmentValues : AssignmentContextType = {
        activePart: activePart,
        setActivePart: setActivePart,
        restartAssignment: restartAssignment,
        moduleName: state.assignment.moduleName,
        lessonVariety: state.assignment.lessonVariety,
        type: state.assignment.type,
        guidedLesson: state.assignment.lesson,
        assessmentOnly: initActivePart === "assessment",
        setOnReturn: setOnReturn,
        submissionId: submissionId,
        demoMode: false,
        reworkAssignmentMode: state.reworkAssignmentMode,
        isReview: state.isReview,
        block: state.assignment.block
    }

    const assessmentId = String(state.assignment?.assessmentId)
    const topicId = String(state.assignment?.topicId)

    return (
        <>
            <div className="text-start my-3">
                {(activePart !== "assessment" && activePart !== "completed") && 
                    <GuardedLink
                        to={state.assignment.lessonVariety === "Review" ? "/dashboard/student/reviews" : "/dashboard/student/assignments"}
                        onClick={onReturn}
                        shouldGuard={true}
                        style={{ "textDecoration": "none" }}
                    >
                        <i className="fas fa-arrow-left" /> Save and Return to Assignments
                    </GuardedLink>}
            </div>
            <input type="hidden" name="hiddenAssignmentId" value={state.assignment.id} />
            <AssignmentContext.Provider value={assignmentValues}>
                <AssignmentComponent
                    assessmentId={assessmentId}
                    submissionId={submissionId}
                    topicId={topicId}
                    moduleName={state.assignment.moduleName}
                    assessmentNumber={state.assignment.assessmentNumber}
                    lessonVariety={state.assignment.lessonVariety}
                    type={state.assignment.type}
                    requireCompletion={state.assignment.requireCompletion}
                    goBack={goBack}
                    continueAssessment={continueAssessment}
                    oneQuestionMode={state.assignment.oneQuestionMode}
                    randomizable={state.assignment.randomizable}
                    reviewAnswerSetting={state.assignment.studentAssessmentViewOption}
                />
            </AssignmentContext.Provider>
        </>
    )
}
